import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SectionCard from '@/components/feature/SectionCard';
import TeamBadge from '@/components/base/TeamBadge';
import MatchRow from '@/components/feature/MatchRow';
import NewsRow from '@/components/feature/NewsRow';
import { useSEO } from '@/hooks/useSEO';
import { searchAll } from '@/utils/data';

const hotKeywords = ['湖人', '勇士', '辽宁', 'NBA', 'CBA', '总决赛', '排名'];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') || '';
  const [input, setInput] = useState(q);

  useSEO({
    title: q ? `“${q}” 搜索结果 - 篮球比分` : '搜索 - 篮球比分',
    description: q
      ? `与“${q}”相关的赛事、球队、球员与新闻搜索结果汇总。`
      : '在篮球比分站内搜索赛事、球队、球员与新闻。',
    keywords: '篮球搜索,赛事搜索,球队搜索,球员搜索',
  });

  const results = searchAll(q);
  const totalCount =
    results.leagues.length + results.teams.length + results.players.length + results.matches.length + results.news.length;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setParams(input.trim() ? { q: input.trim() } : {});
  };

  return (
    <div className="px-4 md:px-6 py-6 animate-fade-up">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索' }]} />

      <header className="mt-4 mb-6">
        <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
          {q ? `“${q}” 的搜索结果` : '站内搜索'}
        </h1>
        <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-3 max-w-xl">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm flex items-center justify-center w-4 h-4">
              <i className="ri-search-line"></i>
            </span>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入赛事、球队或球员关键词"
              className="w-full h-11 pl-9 pr-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>
          <button
            type="submit"
            className="h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </form>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-foreground-500">热门搜索：</span>
          {hotKeywords.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => {
                setInput(k);
                setParams({ q: k });
              }}
              className="px-2.5 py-1 rounded-full bg-background-100 text-xs text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer"
            >
              {k}
            </button>
          ))}
        </div>
      </header>

      {!q ? (
        <div className="bg-background-50 border border-dashed border-background-300 rounded-lg py-16 flex flex-col items-center text-center px-4">
          <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-search-line text-2xl"></i>
          </span>
          <h2 className="font-heading font-bold text-lg text-foreground-900">输入关键词开始搜索</h2>
          <p className="mt-2 text-sm text-foreground-500 max-w-md">支持赛事、球队、球员与新闻的站内检索，试试上面的热门搜索吧。</p>
        </div>
      ) : totalCount === 0 ? (
        <div className="bg-background-50 border border-dashed border-background-300 rounded-lg py-16 flex flex-col items-center text-center px-4">
          <span className="w-14 h-14 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
            <i className="ri-emotion-sad-line text-2xl"></i>
          </span>
          <h2 className="font-heading font-bold text-lg text-foreground-900">未找到与“{q}”相关的内容</h2>
          <p className="mt-2 text-sm text-foreground-500 max-w-md">请尝试更换关键词，或从下方的赛事分类浏览。</p>
          <Link to="/category/all" className="mt-5 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold flex items-center cursor-pointer whitespace-nowrap">
            浏览全部赛事
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          <p className="text-sm text-foreground-500">
            共找到 <span className="text-primary-600 font-semibold">{totalCount}</span> 条相关结果
          </p>

          {results.matches.length > 0 && (
            <SectionCard title={`相关比赛（${results.matches.length}）`} icon="ri-basketball-line">
              <div>
                {results.matches.slice(0, 6).map((m) => (
                  <MatchRow key={m.id} match={m} />
                ))}
              </div>
            </SectionCard>
          )}

          {results.teams.length > 0 && (
            <SectionCard title={`相关球队（${results.teams.length}）`} icon="ri-team-line">
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {results.teams.map((t) => (
                  <Link
                    key={t.id}
                    to={`/team/${t.id}`}
                    className="flex items-center gap-3 p-3 rounded-md border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-colors cursor-pointer"
                  >
                    <TeamBadge team={t} size="md" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground-900 truncate">{t.name}</p>
                      <p className="text-xs text-foreground-500 truncate">{t.leagueName} · {t.city}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </SectionCard>
          )}

          {results.players.length > 0 && (
            <SectionCard title={`相关球员（${results.players.length}）`} icon="ri-user-star-line">
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {results.players.map((p) => (
                  <Link
                    key={p.id}
                    to={`/player/${p.id}`}
                    className="flex items-center gap-3 p-3 rounded-md border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-colors cursor-pointer"
                  >
                    <span className="w-10 h-10 rounded-full bg-secondary-500 flex items-center justify-center font-heading font-bold text-white shrink-0">
                      {p.number}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground-900 truncate">{p.name}</p>
                      <p className="text-xs text-foreground-500 truncate">{p.position} · 场均 {p.ppg} 分</p>
                    </div>
                  </Link>
                ))}
              </div>
            </SectionCard>
          )}

          {results.leagues.length > 0 && (
            <SectionCard title={`相关联赛（${results.leagues.length}）`} icon="ri-trophy-line">
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {results.leagues.map((l) => (
                  <Link
                    key={l.id}
                    to={`/category/${l.id}`}
                    className="flex items-center gap-3 p-3 rounded-md border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-colors cursor-pointer"
                  >
                    <span className="w-10 h-10 rounded-md bg-primary-500 flex items-center justify-center text-background-50 shrink-0">
                      <i className="ri-basketball-line text-lg"></i>
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground-900 truncate">{l.name}</p>
                      <p className="text-xs text-foreground-500 truncate">{l.region} · {l.season}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </SectionCard>
          )}

          {results.news.length > 0 && (
            <SectionCard title={`相关新闻（${results.news.length}）`} icon="ri-newspaper-line">
              <div>
                {results.news.slice(0, 6).map((n) => (
                  <NewsRow key={n.id} news={n} />
                ))}
              </div>
            </SectionCard>
          )}
        </div>
      )}
    </div>
  );
}